'use client';

import {
	AlertTriangle,
	BadgeCheck,
	CheckCircle2,
	Clock3,
	HeartHandshake,
	MessageSquareText,
	Plus,
	RefreshCw,
	Send,
	Sparkles,
	Target
} from 'lucide-react';
import type { ComponentType, FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { api, getErrorMessage } from '$lib/api/client';
import type {
	CloseEngagementPulseRequest,
	CreateEngagementPulseRequest,
	CreateRecognitionRequest,
	EngagementChecklistItem,
	EngagementChecklistStatus,
	EngagementPlaybook,
	EngagementPulseInsight,
	EngagementPulseStatus,
	EngagementRiskItem,
	EngagementRiskSeverity,
	EngagementSummaryResponse,
	EngagementTopic,
	RecognitionResponse,
	RecognitionStatus,
	RecognitionType,
	RecognitionVisibility
} from '$lib/api/types';
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	EmptyState,
	Input,
	Label,
	PageHeader,
	Spinner
} from '$lib/ui';

type RequestState<T> =
	| { status: 'idle' | 'loading' }
	| { status: 'success'; data: T }
	| { status: 'error'; message: string };

type PulseFormState = {
	topic: EngagementTopic;
	title: string;
	targetGroup: string;
	dueDate: string;
};

type RecognitionFormState = {
	type: RecognitionType;
	visibility: RecognitionVisibility;
	fromEmployeeId: string;
	toEmployeeId: string;
	toEmployeeName: string;
	valueTag: string;
	message: string;
};

function numberLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
	return new Intl.NumberFormat('ko-KR').format(value);
}

function scoreLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '0.0';
	return value.toFixed(1);
}

function defaultDueDate(days = 14): string {
	const date = new Date();
	date.setDate(date.getDate() + days);
	return date.toISOString().slice(0, 10);
}

function dateLabel(value: string | null | undefined): string {
	if (!value) return '-';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat('ko-KR', { month: '2-digit', day: '2-digit' }).format(date);
}

function topicLabel(topic: EngagementTopic): string {
	const labels: Record<EngagementTopic, string> = {
		ENGAGEMENT: '몰입도',
		CULTURE: '조직문화',
		LEADERSHIP: '리더십',
		WORKLOAD: '업무부하',
		WELLBEING: '웰빙',
		RETENTION: '리텐션'
	};
	return labels[topic] ?? topic;
}

function pulseStatusLabel(status: EngagementPulseStatus): string {
	const labels: Record<EngagementPulseStatus, string> = {
		OPEN: '수집중',
		CLOSED: '마감',
		CANCELLED: '취소'
	};
	return labels[status] ?? status;
}

function recognitionTypeLabel(type: RecognitionType): string {
	const labels: Record<RecognitionType, string> = {
		PRAISE: '칭찬',
		FEEDBACK: '피드백',
		VALUE: '핵심가치',
		IMPACT: '임팩트',
		ANNIVERSARY: '기념일'
	};
	return labels[type] ?? type;
}

function visibilityLabel(visibility: RecognitionVisibility): string {
	return visibility === 'PUBLIC' ? '공개' : '비공개';
}

function recognitionStatusLabel(status: RecognitionStatus): string {
	const labels: Record<RecognitionStatus, string> = {
		DRAFT: '초안',
		PUBLISHED: '게시',
		ARCHIVED: '보관'
	};
	return labels[status] ?? status;
}

function checklistStatusLabel(status: EngagementChecklistStatus): string {
	if (status === 'READY') return '준비';
	if (status === 'WARNING') return '주의';
	return '대기';
}

function checklistVariant(status: EngagementChecklistStatus): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function riskVariant(severity: EngagementRiskSeverity): 'success' | 'warning' | 'outline' {
	if (severity === 'READY') return 'success';
	if (severity === 'WARNING') return 'warning';
	return 'outline';
}

function statusVariant(status: EngagementPulseStatus | RecognitionStatus): 'success' | 'warning' | 'outline' {
	if (status === 'OPEN' || status === 'DRAFT') return 'warning';
	if (status === 'CLOSED' || status === 'PUBLISHED') return 'success';
	return 'outline';
}

function StatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: number | string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="flex min-h-32 flex-col justify-between p-4">
				<div className="flex items-center justify-between gap-3">
					<p className="text-xs font-medium text-muted-foreground">{title}</p>
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
				</div>
				<div>
					<p className="text-2xl font-semibold tracking-normal">
						{typeof value === 'number' ? numberLabel(value) : value}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function ChecklistGrid({ items }: { items: EngagementChecklistItem[] }) {
	if (items.length === 0) return <EmptyState message="몰입 운영 체크리스트가 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
			{items.map((item) => {
				const Icon = item.status === 'READY' ? CheckCircle2 : item.status === 'WARNING' ? AlertTriangle : Clock3;
				return (
					<div key={item.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
						<div className="flex items-start justify-between gap-3">
							<div className="flex min-w-0 items-start gap-3">
								<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
									<Icon className="size-4" />
								</div>
								<div className="min-w-0">
									<p className="font-semibold">{item.label}</p>
									<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
								</div>
							</div>
							<Badge variant={checklistVariant(item.status)}>{checklistStatusLabel(item.status)}</Badge>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function RiskPanel({ risks }: { risks: EngagementRiskItem[] }) {
	if (risks.length === 0) return <EmptyState message="표시할 몰입 리스크가 없습니다." />;
	return (
		<div className="min-w-0 rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">몰입 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={riskVariant(risk.severity)}>{numberLabel(risk.count)}</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function PulseTable({
	pulses,
	actionId,
	onClose
}: {
	pulses: EngagementPulseInsight[];
	actionId: number | null;
	onClose: (pulse: EngagementPulseInsight) => void;
}) {
	if (pulses.length === 0) return <EmptyState message="등록된 펄스 설문이 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[760px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">펄스</th>
						<th className="px-4 py-3 font-medium">응답</th>
						<th className="px-4 py-3 font-medium">점수</th>
						<th className="px-4 py-3 font-medium">리스크</th>
						<th className="px-4 py-3 font-medium">작업</th>
					</tr>
				</thead>
				<tbody>
					{pulses.map((pulse) => (
						<tr key={pulse.id} className="border-t border-border align-top">
							<td className="px-4 py-4">
								<div className="flex flex-wrap items-center gap-2">
									<p className="font-semibold">{pulse.title}</p>
									<Badge variant={statusVariant(pulse.status)}>{pulseStatusLabel(pulse.status)}</Badge>
								</div>
								<p className="mt-1 text-xs text-muted-foreground">
									{topicLabel(pulse.topic)} · {pulse.targetGroup}
								</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{numberLabel(pulse.responseCount)}명</td>
							<td className="px-4 py-4 text-muted-foreground">{scoreLabel(pulse.averageScore)}</td>
							<td className="px-4 py-4">
								<div className="flex flex-wrap items-center gap-2">
									<Badge variant={pulse.riskLabel === '주의' ? 'warning' : 'outline'}>{pulse.riskLabel}</Badge>
									<span className="text-xs text-muted-foreground">비추천 {numberLabel(pulse.detractorCount)}명</span>
								</div>
							</td>
							<td className="px-4 py-4">
								<Button
									type="button"
									size="sm"
									variant="outline"
									disabled={pulse.status !== 'OPEN' || actionId === pulse.id}
									onClick={() => onClose(pulse)}
								>
									<BadgeCheck className="size-4" />
									마감
								</Button>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function RecognitionFeed({
	recognitions,
	actionId,
	onPublish
}: {
	recognitions: RecognitionResponse[];
	actionId: number | null;
	onPublish: (recognition: RecognitionResponse) => void;
}) {
	if (recognitions.length === 0) return <EmptyState message="등록된 인정·피드백이 없습니다." />;
	return (
		<div className="space-y-3">
			{recognitions.map((recognition) => (
				<div key={recognition.id} className="min-w-0 rounded-lg border border-border bg-background p-4">
					<div className="flex flex-wrap items-start justify-between gap-3">
						<div className="min-w-0">
							<div className="flex flex-wrap items-center gap-2">
								<p className="font-semibold">{recognition.toEmployeeName}</p>
								<Badge variant="outline">{recognitionTypeLabel(recognition.type)}</Badge>
								<Badge variant={recognition.visibility === 'PUBLIC' ? 'success' : 'outline'}>
									{visibilityLabel(recognition.visibility)}
								</Badge>
								<Badge variant={statusVariant(recognition.status)}>{recognitionStatusLabel(recognition.status)}</Badge>
							</div>
							<p className="mt-2 text-sm text-muted-foreground">{recognition.message}</p>
							<div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
								{recognition.valueTag ? <Badge variant="outline">{recognition.valueTag}</Badge> : null}
								<span>게시 {dateLabel(recognition.publishedAt)}</span>
							</div>
						</div>
						<Button
							type="button"
							size="sm"
							variant="outline"
							disabled={recognition.status === 'PUBLISHED' || actionId === recognition.id}
							onClick={() => onPublish(recognition)}
						>
							<Send className="size-4" />
							게시
						</Button>
					</div>
				</div>
			))}
		</div>
	);
}

function PlaybookGrid({ playbooks }: { playbooks: EngagementPlaybook[] }) {
	if (playbooks.length === 0) return <EmptyState message="운영 플레이북이 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
			{playbooks.map((playbook) => (
				<div key={playbook.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
					<div className="flex items-start gap-3">
						<div className="rounded-md bg-muted p-2 text-muted-foreground">
							<Sparkles className="size-4" />
						</div>
						<div className="min-w-0">
							<p className="font-semibold">{playbook.label}</p>
							<p className="mt-1 text-sm text-muted-foreground">{playbook.description}</p>
						</div>
					</div>
				</div>
			))}
		</div>
	);
}

export function EngagementPage() {
	const [state, setState] = useState<RequestState<EngagementSummaryResponse>>({ status: 'idle' });
	const [actionMessage, setActionMessage] = useState<string | null>(null);
	const [operationLoading, setOperationLoading] = useState<string | null>(null);
	const [pulseActionId, setPulseActionId] = useState<number | null>(null);
	const [recognitionActionId, setRecognitionActionId] = useState<number | null>(null);
	const [pulseForm, setPulseForm] = useState<PulseFormState>({
		topic: 'ENGAGEMENT',
		title: '',
		targetGroup: '전체',
		dueDate: defaultDueDate()
	});
	const [recognitionForm, setRecognitionForm] = useState<RecognitionFormState>({
		type: 'VALUE',
		visibility: 'PUBLIC',
		fromEmployeeId: '',
		toEmployeeId: '',
		toEmployeeName: '',
		valueTag: '',
		message: ''
	});

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const summary = await api.get<EngagementSummaryResponse>('/engagement/summary');
				setState({ status: 'success', data: summary });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function submitPulse(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setOperationLoading('pulse');
		setActionMessage(null);
		try {
			const request: CreateEngagementPulseRequest = {
				topic: pulseForm.topic,
				title: pulseForm.title.trim(),
				targetGroup: pulseForm.targetGroup.trim(),
				dueDate: pulseForm.dueDate || null
			};
			await api.post('/engagement/pulses', request);
			setPulseForm((current) => ({
				...current,
				title: '',
				targetGroup: '전체',
				dueDate: defaultDueDate()
			}));
			setActionMessage('펄스 설문을 생성했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function closePulse(pulse: EngagementPulseInsight) {
		setPulseActionId(pulse.id);
		setActionMessage(null);
		try {
			const request: CloseEngagementPulseRequest = {
				responseCount: pulse.responseCount,
				averageScore: pulse.averageScore ?? 0,
				detractorCount: pulse.detractorCount,
				promoterCount: Math.max(0, Math.round(pulse.responseCount * 0.3)),
				summaryNote: `${pulse.title} 설문 결과를 몰입 운영 화면에서 마감했습니다.`
			};
			await api.patch(`/engagement/pulses/${pulse.id}/close`, request);
			setActionMessage('펄스 설문을 마감했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setPulseActionId(null);
		}
	}

	async function submitRecognition(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setOperationLoading('recognition');
		setActionMessage(null);
		try {
			const request: CreateRecognitionRequest = {
				type: recognitionForm.type,
				visibility: recognitionForm.visibility,
				fromEmployeeId: Number(recognitionForm.fromEmployeeId),
				toEmployeeId: Number(recognitionForm.toEmployeeId),
				toEmployeeName: recognitionForm.toEmployeeName.trim(),
				valueTag: recognitionForm.valueTag.trim() || null,
				message: recognitionForm.message.trim()
			};
			await api.post('/engagement/recognitions', request);
			setRecognitionForm((current) => ({
				...current,
				toEmployeeId: '',
				toEmployeeName: '',
				valueTag: '',
				message: ''
			}));
			setActionMessage('인정·피드백 초안을 등록했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function publishRecognition(recognition: RecognitionResponse) {
		setRecognitionActionId(recognition.id);
		setActionMessage(null);
		try {
			await api.patch(`/engagement/recognitions/${recognition.id}/publish`);
			setActionMessage('인정·피드백을 게시했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setRecognitionActionId(null);
		}
	}

	const data = state.status === 'success' ? state.data : null;

	return (
		<div className="min-w-0 space-y-6 overflow-x-hidden">
			<PageHeader
				title="몰입"
				description="펄스 설문, Employee Listening, 인정·칭찬, 비공개 피드백과 리텐션 신호를 함께 운영합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{actionMessage ? (
				<div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">{actionMessage}</div>
			) : null}

			{data ? (
				<>
					<div className="grid min-w-0 grid-cols-2 gap-3 lg:grid-cols-4">
						<StatCard
							title="활성 펄스"
							value={data.openPulseCount}
							description={`전체 ${numberLabel(data.totalPulseCount)}개 캠페인`}
							icon={MessageSquareText}
						/>
						<StatCard
							title="평균 몰입도"
							value={scoreLabel(data.averageEngagementScore)}
							description={`${numberLabel(data.totalResponseCount)}명 응답`}
							icon={Target}
						/>
						<StatCard
							title="인정·칭찬"
							value={data.publishedRecognitionCount}
							description={`비공개 피드백 ${numberLabel(data.privateFeedbackCount)}건`}
							icon={HeartHandshake}
						/>
						<StatCard
							title="문화 리스크"
							value={data.cultureRiskCount}
							description="낮은 점수와 비추천 신호"
							icon={AlertTriangle}
						/>
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-2">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>펄스 설문 생성</CardTitle>
								<CardDescription>조직문화, 리더십, 업무부하, 웰빙, 리텐션 신호를 정기적으로 수집합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<form className="grid gap-3 sm:grid-cols-2" onSubmit={submitPulse}>
									<div>
										<Label htmlFor="engagement-topic">주제</Label>
										<select
											id="engagement-topic"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={pulseForm.topic}
											onChange={(event) =>
												setPulseForm((current) => ({
													...current,
													topic: event.target.value as EngagementTopic
												}))
											}
										>
											<option value="ENGAGEMENT">몰입도</option>
											<option value="CULTURE">조직문화</option>
											<option value="LEADERSHIP">리더십</option>
											<option value="WORKLOAD">업무부하</option>
											<option value="WELLBEING">웰빙</option>
											<option value="RETENTION">리텐션</option>
										</select>
									</div>
									<div>
										<Label htmlFor="engagement-target">대상</Label>
										<Input
											id="engagement-target"
											value={pulseForm.targetGroup}
											onChange={(event) => setPulseForm((current) => ({ ...current, targetGroup: event.target.value }))}
											required
										/>
									</div>
									<div className="sm:col-span-2">
										<Label htmlFor="engagement-title">설문명</Label>
										<Input
											id="engagement-title"
											value={pulseForm.title}
											onChange={(event) => setPulseForm((current) => ({ ...current, title: event.target.value }))}
											placeholder="예: 분기 몰입도"
											required
										/>
									</div>
									<div>
										<Label htmlFor="engagement-due-date">마감일</Label>
										<Input
											id="engagement-due-date"
											type="date"
											value={pulseForm.dueDate}
											onChange={(event) => setPulseForm((current) => ({ ...current, dueDate: event.target.value }))}
										/>
									</div>
									<div className="flex items-end">
										<Button type="submit" className="w-full" disabled={operationLoading === 'pulse'}>
											<Plus className="size-4" />
											설문 생성
										</Button>
									</div>
								</form>
							</CardContent>
						</Card>

						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>인정·피드백 등록</CardTitle>
								<CardDescription>공개 인정과 비공개 성장 피드백을 분리해 구성원 경험을 관리합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<form className="grid gap-3 sm:grid-cols-2" onSubmit={submitRecognition}>
									<div>
										<Label htmlFor="recognition-type">유형</Label>
										<select
											id="recognition-type"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={recognitionForm.type}
											onChange={(event) =>
												setRecognitionForm((current) => ({
													...current,
													type: event.target.value as RecognitionType
												}))
											}
										>
											<option value="PRAISE">칭찬</option>
											<option value="FEEDBACK">피드백</option>
											<option value="VALUE">핵심가치</option>
											<option value="IMPACT">임팩트</option>
											<option value="ANNIVERSARY">기념일</option>
										</select>
									</div>
									<div>
										<Label htmlFor="recognition-visibility">공개 범위</Label>
										<select
											id="recognition-visibility"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={recognitionForm.visibility}
											onChange={(event) =>
												setRecognitionForm((current) => ({
													...current,
													visibility: event.target.value as RecognitionVisibility
												}))
											}
										>
											<option value="PUBLIC">공개</option>
											<option value="PRIVATE">비공개</option>
										</select>
									</div>
									<div>
										<Label htmlFor="recognition-from">보낸 직원 ID</Label>
										<Input
											id="recognition-from"
											type="number"
											value={recognitionForm.fromEmployeeId}
											onChange={(event) =>
												setRecognitionForm((current) => ({ ...current, fromEmployeeId: event.target.value }))
											}
											required
										/>
									</div>
									<div>
										<Label htmlFor="recognition-to">받는 직원 ID</Label>
										<Input
											id="recognition-to"
											type="number"
											value={recognitionForm.toEmployeeId}
											onChange={(event) =>
												setRecognitionForm((current) => ({ ...current, toEmployeeId: event.target.value }))
											}
											required
										/>
									</div>
									<div>
										<Label htmlFor="recognition-name">받는 사람</Label>
										<Input
											id="recognition-name"
											value={recognitionForm.toEmployeeName}
											onChange={(event) =>
												setRecognitionForm((current) => ({ ...current, toEmployeeName: event.target.value }))
											}
											required
										/>
									</div>
									<div>
										<Label htmlFor="recognition-value">가치 태그</Label>
										<Input
											id="recognition-value"
											value={recognitionForm.valueTag}
											onChange={(event) =>
												setRecognitionForm((current) => ({ ...current, valueTag: event.target.value }))
											}
											placeholder="예: 고객 집착"
										/>
									</div>
									<div className="sm:col-span-2">
										<Label htmlFor="recognition-message">내용</Label>
										<textarea
											id="recognition-message"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 min-h-24 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={recognitionForm.message}
											onChange={(event) =>
												setRecognitionForm((current) => ({ ...current, message: event.target.value }))
											}
											required
										/>
									</div>
									<div className="sm:col-span-2">
										<Button type="submit" className="w-full" disabled={operationLoading === 'recognition'}>
											<Plus className="size-4" />
											인정·피드백 등록
										</Button>
									</div>
								</form>
							</CardContent>
						</Card>
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,0.65fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>몰입 운영 체크리스트</CardTitle>
								<CardDescription>설문, 인정, 피드백, 핵심가치, 리텐션 감지가 운영 가능한 상태인지 확인합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<ChecklistGrid items={data.checklist} />
							</CardContent>
						</Card>
						<RiskPanel risks={data.riskItems} />
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>펄스 설문 인사이트</CardTitle>
								<CardDescription>최근 설문별 응답, 평균 점수, 비추천 신호를 확인합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<PulseTable
									pulses={data.pulseInsights}
									actionId={pulseActionId}
									onClose={(pulse) => void closePulse(pulse)}
								/>
							</CardContent>
						</Card>

						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>인정·피드백 피드</CardTitle>
								<CardDescription>핵심가치 실천과 성장 피드백을 게시 상태별로 관리합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<RecognitionFeed
									recognitions={data.recentRecognitions}
									actionId={recognitionActionId}
									onPublish={(recognition) => void publishRecognition(recognition)}
								/>
							</CardContent>
						</Card>
					</div>

					<Card className="min-w-0">
						<CardHeader>
							<CardTitle>운영 플레이북</CardTitle>
							<CardDescription>상용 HR 운영에 필요한 몰입·인정·피드백 루틴을 표준화합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<PlaybookGrid playbooks={data.playbooks} />
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}
